import { CHANNEL_KIND, RCU_ACCEPTED_LOAD_TYPES } from '../../lib/rcu/catalog/channel';
import type { ChannelRef } from '../../lib/rcu/catalog/channel';
import { CONTROL_KIND } from '../../lib/rcu/catalog/control';
import type { ControlKind } from '../../lib/rcu/catalog/control';
import { firmwareChannel } from '../../lib/rcu/catalog/dinValue';
import { RCU_OUTPUT_RANGE } from '../../lib/rcu/catalog/limits';
import { LOAD_TYPE_SPECS } from '../../lib/rcu/catalog/loadType';
import type { LoadTypeKey } from '../../lib/rcu/catalog/loadType';
import { moduleIdOfRail } from '../../lib/rcu/catalog/protocol';
import { RAIL_TYPE_SPECS } from '../../lib/rcu/catalog/railType';
import { assertNever } from '../../lib/exhaustive';
import type { DinRail, RcuConfig } from '../../lib/rcu/model';
import { loadAtChannel } from '../../lib/rcu/query/lookup';
import { channelsOf } from '../../lib/rcu/query/targets';
import { TARGET_VALUE_KIND } from '../../features/keypads/catalog/value';
import type { TargetValueKind } from '../../features/keypads/catalog/value';
import type { MappingTarget, TargetSelector } from '../../features/keypads/targets';

/**
 * The outputs a keypad key may drive, read from the RCU definition.
 *
 * This is the whole of the seam between the two halves of the console: the keypad
 * feature stores an output id and a value, and everything it knows about what
 * those mean — how the output is spelled in a mapping action, what kind of value
 * it takes, what it is called — arrives through here. The feature keeps no copy
 * of the configuration, so a rail that is removed or rewired cannot leave a stale
 * target behind it.
 *
 * The id is the *channel*, not the load: a mapping acts on an output, and a load
 * may be rewired to another output, or renamed, without invalidating every keypad
 * mapping in the building.
 */

/** Stable id for one output, readable in a stored book and in a log line. */
export const targetIdOf = (ref: ChannelRef): string =>
  ref.kind === CHANNEL_KIND.RCU
    ? `${CHANNEL_KIND.RCU}:${ref.channel}`
    : `${CHANNEL_KIND.DIN}:${ref.railId}:${ref.channel}`;

/** What kind of value an output takes, from what it accepts. */
const valueKindOfControl = (control: ControlKind): TargetValueKind => {
  switch (control) {
    case CONTROL_KIND.TOGGLE:
      return TARGET_VALUE_KIND.ON_OFF;
    case CONTROL_KIND.LEVEL:
      return TARGET_VALUE_KIND.PERCENT;
    case CONTROL_KIND.POSITION:
      return TARGET_VALUE_KIND.TRAVEL;
    default:
      return assertNever(control);
  }
};

/**
 * The kind of value an output takes, from the output itself rather than from the
 * load wired to it: rewiring a channel does not change what the channel is.
 */
const valueKindFor = (acceptedLoadTypes: readonly LoadTypeKey[]): TargetValueKind => {
  const first = acceptedLoadTypes[0];
  return first === undefined
    ? TARGET_VALUE_KIND.ON_OFF
    : valueKindOfControl(LOAD_TYPE_SPECS[first].control);
};

/** How a relay output is spelled in a mapping action: `relay:value`. */
const relaySelector = (channel: number): TargetSelector => ({
  kind: CHANNEL_KIND.RCU,
  relay: channel,
});

/** How a DIN channel is spelled: `module,address,channel` and a value. */
const dinSelector = (rail: DinRail, channel: number): TargetSelector => ({
  kind: CHANNEL_KIND.DIN,
  module: moduleIdOfRail(rail.type),
  address: rail.address,
  channel: firmwareChannel(channel),
});

const describe = (label: string, loadName: string | undefined): string =>
  loadName === undefined ? `${label} · no load wired` : `${label} · ${loadName}`;

/** One onboard relay output. */
const rcuOutput = (config: RcuConfig, channel: number): MappingTarget => {
  const ref: ChannelRef = { kind: CHANNEL_KIND.RCU, channel };
  const load = loadAtChannel(config, ref);
  const label = `RCU output ${channel}`;
  return {
    id: targetIdOf(ref),
    label: load?.name ?? label,
    detail: describe(label, load?.name),
    valueKind: valueKindFor(RCU_ACCEPTED_LOAD_TYPES),
    selector: relaySelector(channel),
  };
};

/** One channel of one DIN rail. */
const railOutput = (config: RcuConfig, rail: DinRail, channel: number): MappingTarget => {
  const ref: ChannelRef = { kind: CHANNEL_KIND.DIN, railId: rail.id, channel };
  const load = loadAtChannel(config, ref);
  const spec = RAIL_TYPE_SPECS[rail.type];
  const label = `${spec.shortLabel} @${rail.address} · channel ${channel}`;
  return {
    id: targetIdOf(ref),
    label: load?.name ?? label,
    detail: describe(label, load?.name),
    valueKind: valueKindFor(spec.acceptedLoadTypes),
    selector: dinSelector(rail, channel),
  };
};

/** Every output a keypad key may drive: the RCU's relays, then every rail channel. */
export const keypadTargets = (config: RcuConfig): readonly MappingTarget[] => [
  ...RCU_OUTPUT_RANGE.filter((channel) => channel <= config.rcu.outputCount).map((channel) =>
    rcuOutput(config, channel),
  ),
  ...config.rails.flatMap((rail) =>
    channelsOf(rail).map((channel) => railOutput(config, rail, channel)),
  ),
];
